<section class="ai-section">
    <div class="container">
        <div class="card">
            <div class="page-header">
                <div>
                    <h3 class="page-title">LLM Models</h3>
                    <p class="page-subtitle">Models the agents can use. Only enabled models appear in the agent model picker.</p>
                </div>
                <div class="flex-spacer"></div>
                <button *ngIf="perms.has(P.modelCreate)" class="btn-primary btn-inline" (click)="openCreate()">
                    <i class="fa-solid fa-plus"></i> Add model
                </button>
            </div>

            <div class="toolbar">
                <div class="form-field">
                    <label for="md-filter">Provider</label>
                    <select id="md-filter" [(ngModel)]="providerFilter" (ngModelChange)="onFilterChange()">
                        <option value="">All providers</option>
                        <option *ngFor="let p of providers" [value]="p.id">{{ p.displayName }} ({{ p.code }})</option>
                    </select>
                </div>
            </div>

            <div class="table-wrapper table-mobile-card">
                <table class="table" id="ai-models-table">
                    <thead>
                        <tr>
                            <th>Display name</th>
                            <th>Model ID</th>
                            <th>Provider</th>
                            <th>Context</th>
                            <th>Max output</th>
                            <th>Temperature</th>
                            <th>Vision</th>
                            <th>Tools</th>
                            <th>Thinking levels</th>
                            <th>Price in / out (per 1M)</th>
                            <th class="status-col">Enabled</th>
                            <th class="actions-wide">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngIf="!loading && models.length === 0">
                            <td colspan="100%">
                                <div class="no-data"><i class="fa-solid fa-microchip"></i> No models found</div>
                            </td>
                        </tr>
                        <tr *ngFor="let m of models">
                            <td data-label="Display name"><strong>{{ m.displayName }}</strong></td>
                            <td data-label="Model ID" class="mono">{{ m.modelId }}</td>
                            <td data-label="Provider">{{ providerName(m.providerId) }}</td>
                            <td data-label="Context">{{ m.contextWindow ?? '-' }}</td>
                            <td data-label="Max output">{{ m.maxOutputTokens ?? '-' }}</td>
                            <td data-label="Temperature">{{ m.supportsTemperature ? 'Supported' : 'Not sent' }}</td>
                            <td data-label="Vision"><span [ngClass]="m.supportsVision ? 'badge ok' : 'muted'">{{ m.supportsVision ? 'Images' : 'No' }}</span></td>
                            <td data-label="Tools"><span [ngClass]="supportsTools(m) ? 'badge ok' : 'muted'" [title]="supportsTools(m) ? 'Agents on this model can use playbooks' : 'No tools: agents on this model answer without playbooks'">{{ supportsTools(m) ? 'Playbooks' : 'No' }}</span></td>
                            <td data-label="Thinking levels" class="mono">{{ reasoningSummary(m) }}</td>
                            <td data-label="Price">
                                {{ m.inputPricePer1M ?? '-' }} / {{ m.outputPricePer1M ?? '-' }}
                            </td>
                            <td data-label="Enabled" class="status-col">
                                <span [ngClass]="m.enabled ? 'status-active' : 'status-disabled'">{{ m.enabled ? 'Yes' : 'No' }}</span>
                            </td>
                            <td data-label="Actions" class="actions-wide">
                                <div class="actions-cell">
                                    <button *ngIf="perms.has(P.modelUpdate)" class="icon-btn edit" title="Edit" (click)="openEdit(m)">
                                        <i class="fa-solid fa-pen"></i>
                                    </button>
                                    <button *ngIf="perms.has(P.modelUpdate)" class="icon-btn view" [title]="m.enabled ? 'Disable' : 'Enable'" (click)="toggleEnabled(m)">
                                        <i class="fa-solid" [ngClass]="m.enabled ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
                                    </button>
                                    <button *ngIf="perms.has(P.modelDelete)" class="icon-btn delete" title="Delete" (click)="remove(m)">
                                        <i class="fa-solid fa-trash"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</section>

<div *ngIf="editorOpen" class="dialog-overlay">
    <div class="dialog-card" role="dialog" aria-modal="true">
        <div class="dialog-header">
            <div>
                <h3>{{ editingId ? 'Edit model' : 'Add model' }}</h3>
                <p>The model ID is the exact name the provider API expects (e.g. gpt-5-mini).</p>
            </div>
            <button type="button" class="icon-btn back" (click)="closeEditor()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <form class="dialog-body" (ngSubmit)="save()">
            <div class="form-field">
                <label for="md-provider">Provider</label>
                <select id="md-provider" name="providerId" [(ngModel)]="form.providerId">
                    <option value="" disabled>Select a provider</option>
                    <option *ngFor="let p of providers" [value]="p.id">{{ p.displayName }} ({{ p.code }})</option>
                </select>
            </div>
            <div class="two-col">
                <div class="form-field">
                    <label for="md-modelid">Model ID</label>
                    <input id="md-modelid" name="modelId" [(ngModel)]="form.modelId" maxlength="120" placeholder="gpt-5-mini" />
                </div>
                <div class="form-field">
                    <label for="md-name">Display name</label>
                    <input id="md-name" name="displayName" [(ngModel)]="form.displayName" maxlength="120" placeholder="GPT-5 mini" />
                </div>
            </div>
            <div class="two-col">
                <div class="form-field">
                    <label for="md-ctx">Context window (tokens)</label>
                    <input id="md-ctx" name="contextWindow" type="number" min="0" [(ngModel)]="form.contextWindow" />
                </div>
                <div class="form-field">
                    <label for="md-maxout">Max output tokens</label>
                    <input id="md-maxout" name="maxOutputTokens" type="number" min="0" [(ngModel)]="form.maxOutputTokens" />
                </div>
            </div>
            <div class="two-col">
                <div class="form-field">
                    <label for="md-pin">Input price per 1M tokens</label>
                    <input id="md-pin" name="inputPricePer1M" type="number" min="0" step="0.0001" [(ngModel)]="form.inputPricePer1M" />
                </div>
                <div class="form-field">
                    <label for="md-pout">Output price per 1M tokens</label>
                    <input id="md-pout" name="outputPricePer1M" type="number" min="0" step="0.0001" [(ngModel)]="form.outputPricePer1M" />
                </div>
            </div>
            <label class="check-row">
                <input type="checkbox" name="supportsTemperature" [(ngModel)]="form.supportsTemperature" />
                Supports temperature (when off, temperature is not sent to the provider)
            </label>
            <label class="check-row">
                <input type="checkbox" name="supportsVision" id="md-vision" [(ngModel)]="form.supportsVision" />
                Supports vision (images in the conversation; needed when an agent has image input)
            </label>
            <label class="check-row">
                <input type="checkbox" name="supportsTools" id="md-tools" [(ngModel)]="form.supportsTools" />
                Supports tools (playbooks; when off, no tools and no playbook list are sent and the agent answers as before)
            </label>
            <div class="form-field">
                <label>Thinking levels (reasoning_effort)</label>
                <span class="hint">Tick only the values the provider accepts for this model. None ticked = no thinking level (the field is hidden for the agents).</span>
                <div class="copy-row effort-row">
                    <label class="check-row" *ngFor="let v of effortValues">
                        <input type="checkbox" [name]="'effort-' + v" [ngModel]="hasEffort(v)" (ngModelChange)="toggleEffort(v, $event)" />
                        {{ effortLabels[v] || v }}
                    </label>
                </div>
            </div>
            <div class="form-field" *ngIf="form.reasoningEfforts.length">
                <label for="md-effort-default">Default thinking level</label>
                <select id="md-effort-default" name="defaultReasoningEffort" [(ngModel)]="form.defaultReasoningEffort">
                    <option value="">None (the provider decides)</option>
                    <option *ngFor="let v of form.reasoningEfforts" [value]="v">{{ effortLabels[v] || v }}</option>
                </select>
                <span class="hint">Sent when an agent keeps "Model default".</span>
            </div>
            <label class="check-row"><input type="checkbox" name="enabled" [(ngModel)]="form.enabled" /> Enabled</label>
            <div *ngIf="formError" class="form-field"><span class="error-text">{{ formError }}</span></div>
            <div class="dialog-footer">
                <button type="button" class="btn-light" (click)="closeEditor()">Cancel</button>
                <button type="submit" class="btn-primary">Save</button>
            </div>
        </form>
    </div>
</div>
